/**
 * Angular service for the Media Asset API (/api/media/*)
 *
 * @remarks
 * Handles video (Cloudflare Stream via TUS) and audio (GCS via signed PUT).
 * All authenticated calls attach the Firebase Bearer token via the application's
 * authInterceptor. TUS and GCS upload URLs skip the interceptor automatically
 * because they do not match environment.cmsApiBaseUrl.
 *
 * Configure via MEDIA_ASSET_API_CONFIG in app.config.ts:
 * ```ts
 * { provide: MEDIA_ASSET_API_CONFIG, useValue: { apiBaseUrl: environment.cmsApiBaseUrl } }
 * ```
 */

import { Injectable, inject, InjectionToken } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as tus from 'tus-js-client';

import {
  AudioUploadUrlResponse,
  DeleteMediaAssetResponse,
  GenerateAudioUploadUrlRequest,
  GenerateVideoUploadUrlRequest,
  GetMediaAssetResponse,
  ListMediaAssetsParams,
  ListMediaAssetsResponse,
  MediaAsset,
  MediaDashboardParams,
  MediaDashboardResponse,
  MediaLibraryParams,
  MediaLibraryResponse,
  StorageUsageResponse,
  VideoUploadUrlResponse,
} from '../models/media-asset.model';

// ─── Configuration ────────────────────────────────────────────────────────────

export interface MediaAssetConfig {
  /** Base URL for the CMS API (without trailing slash), e.g. 'http://localhost:3001' */
  apiBaseUrl: string;
  debug?: boolean;
}

export const MEDIA_ASSET_API_CONFIG = new InjectionToken<MediaAssetConfig>(
  'MEDIA_ASSET_API_CONFIG',
  {
    providedIn: 'root',
    factory: () => ({ apiBaseUrl: '', debug: false }),
  }
);

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class MediaAssetService {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(MEDIA_ASSET_API_CONFIG);
  private get _base(): string {
    return `${this._config.apiBaseUrl}/api/media`;
  }
  private get _orgsBase(): string {
    return `${this._config.apiBaseUrl}/api/organizations`;
  }

  private _log(message: string, ...args: unknown[]): void {
    if (this._config.debug) {
      console.log(`[MediaAssetService] ${message}`, ...args);
    }
  }

  // ── Upload URL generation ─────────────────────────────────────────────────

  generateVideoUploadUrl(body: GenerateVideoUploadUrlRequest): Observable<VideoUploadUrlResponse> {
    this._log('Generating video upload URL', body.fileName);
    return this._http.post<VideoUploadUrlResponse>(`${this._base}/upload-url`, body);
  }

  generateAudioUploadUrl(body: GenerateAudioUploadUrlRequest): Observable<AudioUploadUrlResponse> {
    this._log('Generating audio upload URL', body.fileName);
    return this._http.post<AudioUploadUrlResponse>(`${this._base}/upload-url`, body);
  }

  /** Signal the backend to begin FFmpeg processing for an already-uploaded audio asset. */
  triggerProcessing(assetId: string): Observable<void> {
    this._log('Triggering processing for', assetId);
    return this._http.post<void>(`${this._base}/${assetId}/process`, {});
  }

  // ── Cloud uploads (no auth header — interceptor skips non-API URLs) ────────

  /**
   * Upload a video file to Cloudflare Stream via the TUS resumable protocol.
   * The uploadUrl must come from generateVideoUploadUrl().
   */
  uploadVideoViaTus(
    uploadUrl: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const upload = new tus.Upload(file, {
        uploadUrl, // TUS URL pre-created server-side — supports HEAD+PATCH directly
        retryDelays: [0, 3000, 5000, 10000, 20000],
        metadata: { filename: file.name, filetype: file.type },
        onProgress: (uploaded, total) =>
          onProgress?.(Math.round((uploaded / total) * 100)),
        onSuccess: () => resolve(),
        onError: (err) => reject(err),
      });
      upload.start();
    });
  }

  /**
   * Upload an audio file directly to GCS via a signed PUT URL.
   * Uses native XMLHttpRequest (not Angular HttpClient) to avoid Angular's
   * default `Accept` and other headers that can fail GCS CORS preflight checks.
   * Reports progress as percentage (0–100); completes on success, errors on failure.
   *
   * NOTE: The primary CORS fix is a backend GCS bucket CORS configuration that
   * allows the frontend origin. This client-side approach minimises unnecessary
   * headers to reduce the preflight surface.
   */
  uploadAudioToGcs(
    uploadUrl: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Observable<void> {
    return new Observable<void>(observer => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          onProgress?.(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next();
          observer.complete();
        } else {
          observer.error(new Error(`GCS upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => observer.error(new Error('GCS upload network error'));
      xhr.onabort = () => observer.error(new Error('GCS upload aborted'));

      xhr.send(file);

      // Return teardown so Angular can abort if the component is destroyed
      return () => xhr.abort();
    });
  }

  // ── Queries ───────────────────────────────────────────────────────────────

  listAssets(params: ListMediaAssetsParams = {}): Observable<ListMediaAssetsResponse> {
    const httpParams = new HttpParams({ fromObject: this._cleanParams(params as unknown as Record<string, unknown>) });
    this._log('Listing assets', params);
    return this._http.get<ListMediaAssetsResponse>(this._base, { params: httpParams });
  }

  getMediaLibrary(params: MediaLibraryParams = {}): Observable<MediaLibraryResponse> {
    const httpParams = new HttpParams({ fromObject: this._cleanParams(params as unknown as Record<string, unknown>) });
    this._log('Getting media library', params);
    return this._http.get<MediaLibraryResponse>(`${this._base}/library`, { params: httpParams });
  }

  getAsset(id: string, orgId?: string): Observable<GetMediaAssetResponse> {
    const params = orgId ? new HttpParams().set('orgId', orgId) : undefined;
    this._log('Getting asset', id);
    return this._http.get<GetMediaAssetResponse>(`${this._base}/${id}`, { params });
  }

  getDashboard(params: MediaDashboardParams = {}): Observable<MediaDashboardResponse> {
    const httpParams = new HttpParams({ fromObject: this._cleanParams(params as unknown as Record<string, unknown>) });
    this._log('Getting media dashboard');
    return this._http.get<MediaDashboardResponse>(`${this._base}/dashboard`, { params: httpParams });
  }

  getStorageUsage(orgId: string): Observable<StorageUsageResponse> {
    this._log('Getting storage usage for org', orgId);
    return this._http.get<StorageUsageResponse>(`${this._orgsBase}/${orgId}/storage-usage`);
  }

  // ── Mutations ─────────────────────────────────────────────────────────────

  deleteAsset(id: string, orgId?: string): Observable<DeleteMediaAssetResponse> {
    const params = orgId ? new HttpParams().set('orgId', orgId) : undefined;
    this._log('Deleting asset', id);
    return this._http.delete<DeleteMediaAssetResponse>(`${this._base}/${id}`, { params });
  }

  /**
   * Returns true when an error is a delivery-cap 403, regardless of whether it
   * arrives as a raw HttpErrorResponse or as an ApiErrorResponse (post-interceptor).
   */
  isDeliveryLimitError(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false;
    const e = err as Record<string, unknown>;
    // status (HttpErrorResponse) OR statusCode (ApiErrorResponse after interceptor)
    const httpStatus = (e['status'] ?? e['statusCode']) as number | undefined;
    if (httpStatus !== 403) return false;
    // message field (set by extractApiError from body['error'] or body['message'])
    const msg = ((e['message'] ?? '') as string).toLowerCase();
    if (msg.includes('delivery limit')) return true;
    // Raw body fallback when interceptor hasn't run (e.g. unit tests)
    const body = e['error'] as Record<string, unknown> | string | undefined;
    if (typeof body === 'string') return body.toLowerCase().includes('delivery limit');
    if (body && typeof body === 'object') {
      return ((body['error'] ?? '') as string).toLowerCase().includes('delivery limit');
    }
    return false;
  }

  /**
   * Notifies the backend that a video has started playing so delivery minutes
   * can be accounted against the org's monthly quota. Fire-and-forget; errors
   * are intentionally not propagated to callers.
   */
  recordPlaybackEvent(assetId: string): Observable<void> {
    return this._http.post<void>(`${this._base}/${assetId}/playback-event`, {});
  }

//   // ── Polling ───────────────────────────────────────────────────────────────

//   /**
//    * Poll GET /api/media/:id until processingStatus is 'ready' or 'failed'.
//    * Automatically unsubscribes on completion or error.
//    * Timeout after maxAttempts * intervalMs (~5 minutes by default).
//    */
//   pollUntilReady(
//     assetId: string,
//     orgId?: string,
//     intervalMs = 3000,
//     maxAttempts = 100
//   ): Observable<MediaAsset> {
//     return new Observable<MediaAsset>(observer => {
//       let attempts = 0;
//       const timer = setInterval(() => {
//         if (++attempts > maxAttempts) {
//           clearInterval(timer);
//           observer.error(new Error('Polling timeout: asset did not reach ready state'));
//           return;
//         }
//         this.getAsset(assetId, orgId).subscribe({
//           next: ({ data }) => {
//             if (data.processingStatus === 'ready' || data.processingStatus === 'failed') {
//               clearInterval(timer);
//               observer.next(data);
//               observer.complete();
//             }
//           },
//           error: (err: unknown) => {
//             clearInterval(timer);
//             observer.error(err);
//           },
//         });
//       }, intervalMs);
//       return () => clearInterval(timer);
//     });
//   }

  // ── Status stream (SSE — video) ───────────────────────────────────────────

  /**
   * Wait for a video asset to become ready via Server-Sent Events.
   *
   * The backend holds the SSE connection open and pushes exactly one event the
   * moment the Cloudflare webhook fires, then closes the stream. This gives
   * near-instant notification with zero polling pressure on the API.
   *
   * Auth note: EventSource does not support custom headers, so the Firebase ID
   * token is passed as a ?token= query parameter. The backend validates it with
   * verifyIdToken() before opening the stream.
   *
   * Fallback: on network error or a 'timeout' status event from the server, the
   * method transparently switches to pollUntilReady() so the caller always gets
   * a result. The inner fallback subscription is included in the teardown, so
   * unsubscribing the outer observable always cleans up completely.
   *
   * @param assetId    MongoDB _id of the MediaAsset
   * @param orgId      Optional — falls back to the JWT claim server-side
   * @param token      Firebase ID token (from AuthService.getBearerToken())
   * @param onFallback Optional callback invoked once when SSE fails and polling takes over
   */
  waitUntilReady(
    assetId: string,
    orgId?: string,
    token?: string,
    onFallback?: () => void
  ): Observable<MediaAsset> {
    return new Observable<MediaAsset>(observer => {
      let fallbackSub: { unsubscribe(): void } | null = null;
      // Guard: EventSource may fire onerror multiple times before es.close() takes
      // effect (auto-reconnect). Ensure we only start one fallback polling session.
      let hasFalledBack = false;

      const params = new URLSearchParams();
      if (orgId) params.set('orgId', orgId);
      if (token) params.set('token', token);
      const qs = params.toString() ? `?${params}` : '';
      const url = `${this._base}/${assetId}/status-stream${qs}`;

      const fallback = () => {
        if (hasFalledBack) return;
        hasFalledBack = true;
        onFallback?.();
        this._log('SSE falling back to polling for', assetId);
        fallbackSub = this.pollUntilReady(assetId, orgId).subscribe(observer);
      };

      this._log('Opening SSE stream for', assetId);
      const es = new EventSource(url);

      // If the SSE stream connects but the backend never pushes a 'ready' event
      // (e.g. webhook not wired up, backend issue), fall back to polling after 45s.
      const sseTimeoutId = setTimeout(() => {
        console.warn('[MediaAssetService] SSE timeout — no ready event in 45s, falling back to polling for', assetId);
        es.close();
        fallback();
      }, 45_000);



      es.onmessage = (event: MessageEvent) => {
        try {
          const payload = JSON.parse(event.data as string) as {
            processingStatus: string;
            processingError?: string;
            error?: string;
          };

          if (payload.processingStatus === 'ready' || payload.processingStatus === 'failed') {
            clearTimeout(sseTimeoutId);
            es.close();
            // Fetch the full asset so the caller gets playbackInfo included
            this.getAsset(assetId, orgId).subscribe({
              next: ({ data }) => { observer.next(data); observer.complete(); },
              error: (err: unknown) => observer.error(err),
            });
          } else if (payload.processingStatus === 'timeout' || payload.error) {
            clearTimeout(sseTimeoutId);
            es.close();
            fallback();
          }
        } catch {
          es.close();
          observer.error(new Error('SSE parse error'));
        }
      };

      es.onerror = () => {
        clearTimeout(sseTimeoutId);
        es.close();
        fallback();
      };

      // Teardown: close EventSource AND cancel any in-flight fallback polling
      return () => {
        clearTimeout(sseTimeoutId);
        es.close();
        fallbackSub?.unsubscribe();
      };
    });
  }

  // ── Polling (fallback + audio) ─────────────────────────────────────────────

  /** Poll until processingStatus === 'ready' or 'failed', with exponential backoff.
   *
   * Interval schedule: 5s → 8s → 13s → 21s → 30s (capped) — averages ~15s per poll
   * over a 5-minute processing window, keeping total requests well under the rate limit.
   * Timeout: ~5 minutes (20 attempts with backoff).
   */
  pollUntilReady(assetId: string, orgId?: string, maxAttempts = 20): Observable<MediaAsset> {
    return new Observable(observer => {
      let attempts = 0;
      let timeoutId: ReturnType<typeof setTimeout>;

      const poll = () => {
        if (++attempts > maxAttempts) {
          observer.error(new Error('Polling timeout: video processing took too long'));
          return;
        }

        this.getAsset(assetId, orgId).subscribe({
          next: ({ data }) => {
            if (data.processingStatus === 'ready' || data.processingStatus === 'failed') {
              observer.next(data);
              observer.complete();
            } else {
              // Exponential backoff: 5s, 8s, 13s, 21s, 30s (capped)
              const delay = Math.min(5000 * Math.pow(1.6, attempts - 1), 30000);
              timeoutId = setTimeout(poll, delay);
            }
          },
          error: (err: unknown) => observer.error(err),
        });
      };

      timeoutId = setTimeout(poll, 5000); // first check after 5s
      return () => clearTimeout(timeoutId);
    });
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  private _cleanParams(params: Record<string, unknown>): Record<string, string> {
    return Object.fromEntries(
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, String(v)])
    );
  }
}

// ─── Provider factory ─────────────────────────────────────────────────────────

export function provideMediaAssetService(config: MediaAssetConfig): {
  provide: typeof MEDIA_ASSET_API_CONFIG;
  useValue: MediaAssetConfig;
} {
  return { provide: MEDIA_ASSET_API_CONFIG, useValue: config };
}
